import { useNavigate } from "react-router-dom";
import { getUserData } from "../../utils/localStorage";
import { useEffect, useState } from "react";
import { storeOrders } from "../../services/Orders";
import { getRackManualModel } from "../../services/RackModel/RackModel";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import StoreMenu from "../StoreMenu";
import { sortBy } from "lodash";

const { innerHeight: height } = window;

const RackModelReport = () => {
  const navigate = useNavigate();

  const { userProfileRole, storeData, brandProfileData } = getUserData();

  let brandId = brandProfileData.id;
  const storeId = storeData.id;

  const userRole = userProfileRole;

  const [rackModelList, setRackModelList] = useState([]);
  const [rackList, setRackList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const fetchRackModel = (storeId) => {
    setLoading(true);

    const data = {
      sortOrder: "desc",
      sortBy: "id",
      brandId: brandId,
      rackData: true,
      statusIn: ["PROCESSED"],
    };

    storeOrders(storeId, data)
      .then((response) => {
        if (!response.data.error) {
          const Res = response.data;
          setRackModelList(Res);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
        setLoading(false);
      });
  };

  const fetchRackList = (storeId) => {
    getRackManualModel(storeId)
      .then((response) => {
        if (!response.data.error) {
          setRackList(
            Array.isArray(response.data?.racks) ? response.data.racks : [],
          );
        }
      })
      .catch((err) => {
        console.error("Error loading rack list:", err);
      });
  };

  useEffect(() => {
    fetchRackModel(storeId);
    fetchRackList(storeId);
  }, [storeId]);

  const parseRackInfo = (rackInfo) => {
    if (Array.isArray(rackInfo)) {
      return rackInfo.map((rack) => String(rack).trim()).filter(Boolean);
    }
    return typeof rackInfo === "string"
      ? rackInfo
          .split(",")
          .map((rack) => rack.trim())
          .filter(Boolean)
      : [];
  };

  // group orders by rack; an order kept in multiple racks shows under each
  const ordersByRack = {};
  rackList.forEach((rack) => {
    ordersByRack[rack] = [];
  });
  (Array.isArray(rackModelList) ? rackModelList : []).forEach((order) => {
    parseRackInfo(order?.rackInfo).forEach((rack) => {
      if (!ordersByRack[rack]) ordersByRack[rack] = [];
      ordersByRack[rack].push(order);
    });
  });

  const racks = Object.keys(ordersByRack);

  // orders split across more than one rack get a unique color so they can be
  // matched across columns
  const MULTI_RACK_COLORS = [
    "#FFE0B2",
    "#C8E6C9",
    "#F8BBD0",
    "#D1C4E9",
    "#B3E5FC",
    "#FFF59D",
    "#FFCCBC",
    "#B2DFDB",
    "#E1BEE7",
    "#DCEDC8",
  ];
  const multiRackColors = {};
  (Array.isArray(rackModelList) ? rackModelList : [])
    .filter((order) => parseRackInfo(order?.rackInfo).length > 1)
    .forEach((order, index) => {
      multiRackColors[order.id] =
        MULTI_RACK_COLORS[index % MULTI_RACK_COLORS.length];
    });
  const maxRows = Math.max(
    0,
    ...racks.map((rack) => ordersByRack[rack].length),
  );
  const rackRows = Array.from({ length: maxRows }, (_, rowIndex) => {
    const row = {};
    racks.forEach((rack, colIndex) => {
      row[colIndex] = ordersByRack[rack][rowIndex];
    });
    return row;
  });

  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const headers = racks.map(
        (rack) => `${rack} (${ordersByRack[rack].length})`,
      );
      const sheetRows = rackRows.map((row) =>
        racks.map((_, colIndex) => row[colIndex]?.invoiceNo ?? ""),
      );
      const worksheet = xlsx.utils.aoa_to_sheet([headers, ...sheetRows]);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, "Rack_Model_Report");
    });
  };

  const saveAsExcelFile = (buffer, fileName) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });

        module.default.saveAs(
          data,
          fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION,
        );
      }
    });
  };

  const invoiceBodyTemplate = (rowData) => {
    if (!rowData) return null;
    const multiRackColor = multiRackColors[rowData.id];
    return (
      <div
        className="cursor-pointer"
        title={
          multiRackColor
            ? `Racks: ${parseRackInfo(rowData.rackInfo).join(", ")}`
            : undefined
        }
        style={
          multiRackColor
            ? {
                backgroundColor: multiRackColor,
                padding: "2px 6px",
                borderRadius: 4,
                fontWeight: 600,
              }
            : undefined
        }
        onClick={() =>
          userRole === "BY_ADMIN" || userRole === "BY_SUB_ADMIN_USER"
            ? navigate("/OrdersGarmentInfo", {
                state: {
                  storeIdValue: rowData.storeId,
                  bookingOrderId: rowData.id,
                  storeCustomerId: rowData.storeCustomerId,
                },
              })
            : navigate("/OrdersGarmentInfo", {
                state: {
                  bookingOrderId: rowData.id,
                  storeCustomerId: rowData.storeCustomerId,
                  navigateThrough: "GoBack",
                },
              })
        }
      >
        <span style={{ color: "blue", textDecoration: "underline" }}>
          {rowData?.invoiceNo}
        </span>
      </div>
    );
  };

  const RenderTabView = () => {
    return (
      <DataTable
        showGridlines
        value={rackRows}
        paginator
        rows={100}
        rowsPerPageOptions={[100, 150, 200]}
        stripedRows
        scrollable
        scrollHeight={height - 120}
      >
        {racks.map((rack, colIndex) => (
          <Column
            key={rack}
            header={`${rack} (${ordersByRack[rack].length})`}
            style={{
              minWidth: "120px",
              paddingVertical: 1,
              paddingLeft: 5,
              paddingRight: 1,
            }}
            body={(rowData) => invoiceBodyTemplate(rowData[colIndex])}
          />
        ))}
      </DataTable>
    );
  };

  return (
    <div className="flex-1">
      <div
        className="h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center"
        style={{
          "--surface-0": "#1752AA",
        }}
      >
        <div className="flex-1 flex ml-3 mr-3">
          <div className="flex-1 flex ">
            <div className="ml-3 flex justify-content-center align-items-center">
              <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">
                <i
                  className="pi pi-angle-left"
                  onClick={() => navigate("/full-dashboard")}
                  style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}
                ></i>
              </div>
            </div>
            <div className="flex-1 ml-5">
              <p className="text-white text-2xl font-bold uppercase">
                Rack Model Report
              </p>
            </div>
          </div>
          {racks.length > 0 && (
            <div
              className="flex justify-content-center align-items-center cursor-pointer"
              onClick={exportExcel}
              title="Download Excel"
            >
              <i
                className="pi pi-download"
                style={{ fontSize: "24px", marginRight: 25, color: "#fff" }}
              ></i>
            </div>
          )}
          <div
            className="flex justify-content-center align-items-center cursor-pointer"
            onClick={() => setVisible(true)}
          >
            <i
              className="pi pi-bars"
              style={{ fontSize: "27px", marginRight: 30, color: "#fff" }}
            ></i>
          </div>
        </div>
      </div>

      <div className="mx-4 my-2">
        {loading ? (
          <div style={{ flex: 1 }}>
            <div className="text-center">
              <ProgressSpinner
                style={{ width: "70px", height: "70px" }}
                strokeWidth="4"
                fill="var(--surface-ground)"
                animationDuration="2s"
              />
            </div>
          </div>
        ) : racks.length > 0 ? (
          <div>
            <div>
              <div className="scroll-bar mb-2">
                <div className="report-table">
                  <div className="tab-view-table">{RenderTabView()}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-content-center align-items-center">
            <p className="text-xl font-semi-bold">No Rack Found</p>
          </div>
        )}
      </div>

      <StoreMenu setVisible={setVisible} visible={visible} />
    </div>
  );
};

export default RackModelReport;
